import React from 'react';
import { RemittanceRecord } from '../services/api';
import { DollarSign, Calendar, Building, Hash, FileText, BookOpen } from 'lucide-react';

interface RemittanceTableProps {
  records: RemittanceRecord[];
  loading?: boolean;
}

const RemittanceTable: React.FC<RemittanceTableProps> = ({ records, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-4 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 text-center">
        <div className="text-gray-500">
          <DollarSign className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium mb-2">暫無記錄</h3>
          <p className="text-sm">請嘗試調整搜尋條件或檢查資料是否存在</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
        <h3 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center">
          <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 mr-2" />
          匯款記錄列表 ({records.length} 筆) - 莊家羚
        </h3>
      </div>
      
      {/* 手機版 - 卡片式佈局 */}
      <div className="block sm:hidden">
        <div className="divide-y divide-gray-200">
          {records.map((record) => (
            <div key={record.id} className="p-4 hover:bg-gray-50 transition-colors">
              <div className="space-y-3">
                {/* 第一行：ID 和截款日 */}
                <div className="flex justify-between items-start">
                  <div className="flex items-center text-sm text-gray-500">
                    <Hash className="h-3 w-3 mr-1" />
                    {record.id}
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 text-purple-600 mr-1" />
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {record.payment_date}
                    </span>
                  </div>
                </div>
                
                {/* 第二行：客戶名稱 */}
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-sm text-gray-500">客戶</div>
                    <div className="font-medium text-gray-900">{record.customer_name}</div>
                  </div>
                  {record.page && (
                    <div className="flex items-center">
                      <BookOpen className="h-4 w-4 text-indigo-600 mr-1" />
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">
                        {record.page}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* 第三行：匯款單位和銀行 */}
                <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                  <div className="flex items-center">
                    <DollarSign className="h-4 w-4 text-green-600 mr-1" />
                    <span className="text-base font-semibold text-green-600">
                      {record.amount.toLocaleString('zh-TW', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </span>
                  </div>
                  <div className="text-right">
                    {record.bank_name ? (
                      <div className="flex items-center">
                        <Building className="h-4 w-4 text-gray-600 mr-1" />
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                          {record.bank_name}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">無銀行資訊</span>
                    )}
                  </div>
                </div>
                
                {/* 第四行：備註 */}
                {record.note && (
                  <div className="pt-2 border-t border-gray-100">
                    <div className="flex items-start">
                      <FileText className="h-4 w-4 text-orange-600 mr-1 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm text-gray-500">備註</div>
                        <div className="text-sm text-gray-700 bg-orange-50 px-2 py-1 rounded mt-1">
                          {record.note}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 桌面版 - 表格佈局 */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  截款日
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                客戶名稱
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center">
                  <DollarSign className="h-4 w-4 mr-1" />
                  匯款單位
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center">
                  <Building className="h-4 w-4 mr-1" />
                  銀行
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center">
                  <BookOpen className="h-4 w-4 mr-1" />
                  分頁
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center">
                  <FileText className="h-4 w-4 mr-1" />
                  備註
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {records.map((record) => (
              <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  #{record.id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                    {record.payment_date}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {record.customer_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-medium text-green-600">
                    {record.amount.toLocaleString('zh-TW', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {record.bank_name ? (
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                      {record.bank_name}
                    </span>
                  ) : (
                    <span className="text-gray-400 text-sm">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {record.page ? (
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">
                      {record.page}
                    </span>
                  ) : (
                    <span className="text-gray-400 text-sm">-</span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                  {record.note ? (
                    <div className="bg-orange-50 px-2 py-1 rounded text-xs text-gray-700 truncate" title={record.note}>
                      {record.note}
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RemittanceTable; 